import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { v2, type NotificationApi } from '@/api/v2Client';
import { useNotificationsStream } from '@/hooks/useNotificationsStream';
import { NotificationItem } from '@/components/notifications/NotificationItem';

// Phase 5.X-notifications-system — full notifications page.
//
// Surface area:
//   - Filter bar: unread-only toggle + type filter (free text dropdown
//     once we have multiple types; placeholder text input for now).
//   - List of notifications (newest first).
//   - Per-row Mark-read + Delete.
//   - "Load more" pagination via cursor (the API supports `before=<iso>`).
//   - SSE stream pushes prepend in real time.
//   - Empty state when the filter returns zero rows.

const PAGE_STYLE: CSSProperties = {
  padding: 24,
  maxWidth: 760,
  margin: '0 auto',
};

const HEADER_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 16,
};

const FILTER_BAR_STYLE: CSSProperties = {
  display: 'flex',
  gap: 12,
  marginBottom: 16,
  padding: 12,
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: 6,
  background: 'var(--surface, #f9fafb)',
};

const TYPE_FILTER_STYLE: CSSProperties = {
  fontSize: 13,
  padding: '4px 8px',
  border: '1px solid var(--border, #d1d5db)',
  borderRadius: 4,
  background: '#fff',
};

const EMPTY_STYLE: CSSProperties = {
  padding: '48px 16px',
  textAlign: 'center',
  color: 'var(--text-muted, #6b7280)',
  fontSize: 14,
};

const LOAD_MORE_STYLE: CSSProperties = {
  margin: '12px auto 0',
  display: 'block',
  padding: '6px 16px',
  background: 'var(--surface, #fff)',
  border: '1px solid var(--border, #d1d5db)',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};

const PAGE_SIZE = 50;

export function NotificationsPage(): ReactElement {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationApi[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  // Phase notification-count-badge — tell the header BellIcon to re-fetch
  // the count when this page mounts so the badge clears after the user
  // navigates here (they're presumably reading notifications).
  useEffect(() => {
    window.dispatchEvent(new Event('bell:invalidate'));
  }, []);

  const loadPage = useCallback(
    async (reset: boolean): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const before =
          !reset && items.length > 0 ? items[items.length - 1]?.createdAt : undefined;
        const res = await v2.notifications.list({
          unread: unreadOnly,
          limit: PAGE_SIZE,
          ...(before ? { before } : {}),
        });
        setItems((prev) => (reset ? res.notifications : [...prev, ...res.notifications]));
        setHasMore(res.hasMore);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    },
    [items, unreadOnly],
  );

  // Reset + reload when the unread filter changes.
  useEffect(() => {
    void loadPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly]);

  // SSE — push new notifications to the top of the list.
  useNotificationsStream({
    onNotification: (n) => {
      setItems((prev) => {
        if (prev.some((p) => p.id === n.id)) return prev;
        return [n, ...prev];
      });
    },
  });

  const handleItemClick = useCallback(async (n: NotificationApi) => {
    if (n.readAt === null) {
      const optimisticReadAt = new Date().toISOString();
      setItems((prev) =>
        prev.map((p) => (p.id === n.id ? { ...p, readAt: optimisticReadAt } : p)),
      );
      try {
        await v2.notifications.markRead(n.id);
      } catch {
        setItems((prev) =>
          prev.map((p) => (p.id === n.id ? { ...p, readAt: null } : p)),
        );
      }
    }
    if (n.link) navigate(n.link);
  }, [navigate]);

  const handleMarkRead = useCallback(async (n: NotificationApi) => {
    if (n.readAt !== null) return;
    const optimisticReadAt = new Date().toISOString();
    setItems((prev) =>
      prev.map((p) => (p.id === n.id ? { ...p, readAt: optimisticReadAt } : p)),
    );
    try {
      await v2.notifications.markRead(n.id);
    } catch {
      setItems((prev) =>
        prev.map((p) => (p.id === n.id ? { ...p, readAt: null } : p)),
      );
    }
  }, []);

  const handleDelete = useCallback(async (n: NotificationApi) => {
    const snapshot = items;
    setItems((prev) => prev.filter((p) => p.id !== n.id));
    try {
      await v2.notifications.delete(n.id);
    } catch {
      setItems(snapshot);
    }
  }, [items]);

  // Apply the type filter client-side (cheap given page sizes; saves
  // adding a server-side type filter for v1).
  const filtered = typeFilter
    ? items.filter((n) => n.type === typeFilter)
    : items;

  return (
    <div data-testid="notifications-page" style={PAGE_STYLE}>
      <header style={HEADER_STYLE}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Notifications</h1>
      </header>
      <div style={FILTER_BAR_STYLE}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            data-testid="notifications-filter-unread"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          Unread only
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Category (loaded notifications):
          <select data-testid="notifications-filter-type" value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)} style={TYPE_FILTER_STYLE}>
            <option value="">All categories</option>
            {Array.from(new Set(items.map((item) => item.type))).sort().map((type) =>
              <option key={type} value={type}>{type.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}</option>)}
          </select>
        </label>
      </div>
      {error && (
        <div role="alert" data-testid="notifications-error" style={{ color: 'var(--danger, #b91c1c)', marginBottom: 12 }}>
          {error}
        </div>
      )}
      {filtered.length === 0 && !loading && !error && (
        <div data-testid="notifications-empty" style={EMPTY_STYLE}>
          {unreadOnly ? 'No unread notifications.' : 'No notifications yet.'}
        </div>
      )}
      <div>
        {filtered.map((n, index) => (
          <NotificationItem
            key={n.id}
            notification={n}
            alternate={index % 2 === 1}
            onClick={() => void handleItemClick(n)}
            onMarkRead={() => void handleMarkRead(n)}
            onDelete={() => void handleDelete(n)}
          />
        ))}
      </div>
      {hasMore && !loading && (
        <button
          type="button"
          data-testid="notifications-load-more"
          onClick={() => void loadPage(false)}
          style={LOAD_MORE_STYLE}
        >
          Load more
        </button>
      )}
      {loading && (
        <div data-testid="notifications-loading" style={{ ...EMPTY_STYLE, padding: 24 }}>
          Loading…
        </div>
      )}
    </div>
  );
}
